import React, { useEffect, useState, useRef } from 'react';
import './PhrasesCloud.css';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../config/authenticatedFetch';

// Interface para uma frase individual
export interface Phrase {
  id: string;
  phrase: string;
  author?: string;
  category?: string;
  isNew?: boolean;
}

// Interface para posição de frase na tela
interface PhrasePosition {
  id: string;
  phrase: string;
  author?: string;
  x: number;
  y: number;
  rotation: number;
  fontSize: number;
  opacity: number;
  addedTime: number; // Timestamp para monitoramento
}

export interface PhrasesCloudProps {
  maxPhrases?: number;
  minFontSize?: number;
  maxFontSize?: number;
  animationDuration?: number;
  fadeInDuration?: number;
  fadeOutDuration?: number;
  rotationProbability?: number;
  onPhraseClick?: (phrase: Phrase) => void;
  className?: string;
}

const PhrasesCloudSimple: React.FC<PhrasesCloudProps> = ({
  maxPhrases: propMaxPhrases, // Não usaremos mais este valor diretamente
  minFontSize = 18,
  maxFontSize = 40, // Reduzido para textos menores
  animationDuration = 10000, // Reduzido para rotatividade mais rápida
  fadeInDuration = 1000,
  fadeOutDuration = 1000,
  rotationProbability = 0,
  onPhraseClick,
  className = '',
}) => {
  const [phrases, setPhrases] = useState<Phrase[]>([]);
  const [phrasePositions, setPhrasePositions] = useState<PhrasePosition[]>([]);
  const [activePhrases, setActivePhrases] = useState<Set<string>>(new Set());
  const [maxPhrases, setMaxPhrases] = useState<number>(10); // Estado para maxPhrases dinâmico
  const [parametersLoaded, setParametersLoaded] = useState<boolean>(false); // Controle de carregamento
  const containerRef = useRef<HTMLDivElement>(null);
  const animationTimeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const addPhraseIntervalRef = useRef<NodeJS.Timeout>();

  // Buscar parâmetros da API
  const fetchParameters = async () => {
    try {
      console.log('🔧 Buscando parâmetros da API...');
      const response = await authenticatedFetch(API_ENDPOINTS.GET_PARAMETERS, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('⚙️ Parâmetros recebidos da API:', data);

      // Extrair maxPhrases dos parameters com fallback
      const parametersData = data.parameters || {};
      const maxPhrasesParam = parametersData.maxPhrases || '10';
      const maxPhrasesValue = parseInt(maxPhrasesParam, 10);
      const finalMaxPhrases = isNaN(maxPhrasesValue) ? 10 : maxPhrasesValue;
      
      console.log(`📊 maxPhrases definido: ${finalMaxPhrases} (recebido: "${maxPhrasesParam}")`);
      
      setMaxPhrases(finalMaxPhrases);
      setParametersLoaded(true);
      
      return finalMaxPhrases;
    } catch (error) {
      console.error('❌ Erro ao buscar parâmetros:', error);
      // Em caso de erro, usar fallback
      console.log('🔄 Usando valor fallback: 10');
      setMaxPhrases(10);
      setParametersLoaded(true);
      return 10;
    }
  };

  // Buscar frases da API
  const fetchPhrases = async () => {
    try {
      console.log('🔍 Buscando frases da API...');
      const response = await authenticatedFetch(API_ENDPOINTS.GET_PHRASES, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📊 Dados recebidos da API:', data);

      // Combinar frases novas e antigas
      const allPhrases = [...(data.new || []), ...(data.old || [])];
      console.log(`📝 Total de frases: ${allPhrases.length}`);
      
      setPhrases(allPhrases);
      return allPhrases;
    } catch (error) {
      console.error('❌ Erro ao buscar frases:', error);
      return [];
    }
  };

  // Verificar colisão entre duas frases
  const checkCollision = (pos1: PhrasePosition, pos2: PhrasePosition): boolean => {
    const estimateTextDimensions = (text: string, fontSize: number, rotation: number) => {
      const avgCharWidth = fontSize * 0.6;
      const textWidth = text.length * avgCharWidth;
      const textHeight = fontSize * 1.4; // Aumentado para mais espaço vertical
      
      if (Math.abs(rotation) === 90) {
        return { width: textHeight, height: textWidth };
      }
      return { width: textWidth, height: textHeight };
    };

    const dim1 = estimateTextDimensions(pos1.phrase, pos1.fontSize, pos1.rotation);
    const dim2 = estimateTextDimensions(pos2.phrase, pos2.fontSize, pos2.rotation);

    const margin = 40; // Aumentado para mais espaço entre frases

    return !(pos1.x + dim1.width + margin < pos2.x || 
             pos2.x + dim2.width + margin < pos1.x || 
             pos1.y + dim1.height + margin < pos2.y || 
             pos2.y + dim2.height + margin < pos1.y);
  };

  // Gerar posição aleatória sem colisão
  const generateRandomPosition = (phrase: Phrase): PhrasePosition => {
    const containerWidth = window.innerWidth;
    const containerHeight = window.innerHeight;
    
    const fontSize = minFontSize + Math.random() * (maxFontSize - minFontSize);
    const rotation = Math.random() < rotationProbability ? (Math.random() < 0.5 ? 90 : -90) : 0;
    
    // Estimar dimensões do texto para posicionamento mais preciso
    const estimateTextWidth = (text: string, fontSize: number, rotation: number) => {
      const avgCharWidth = fontSize * 0.6;
      const textWidth = text.length * avgCharWidth;
      const textHeight = fontSize * 1.4;
      
      if (Math.abs(rotation) === 90) {
        return { width: textHeight, height: textWidth };
      }
      return { width: textWidth, height: textHeight };
    };
    
    const textDimensions = estimateTextWidth(phrase.phrase, fontSize, rotation);
    
    // Área utilizável considerando as dimensões do texto
    const margin = 50; // Margem das bordas
    const usableWidth = containerWidth - textDimensions.width - (margin * 2);
    const usableHeight = containerHeight - textDimensions.height - (margin * 2);
    
    // Centralizar mais as frases - área central de 60% da tela
    const centralAreaWidth = containerWidth * 0.6;
    const centralAreaHeight = containerHeight * 0.6;
    const centralOffsetX = (containerWidth - centralAreaWidth) / 2;
    const centralOffsetY = (containerHeight - centralAreaHeight) / 2;
    
    // Ajustar para não sair das bordas com as dimensões do texto
    const maxX = Math.min(usableWidth, centralAreaWidth - textDimensions.width) + margin;
    const maxY = Math.min(usableHeight, centralAreaHeight - textDimensions.height) + margin;
    
    let attempts = 0;
    let position: PhrasePosition;
    
    do {
      position = {
        id: phrase.id,
        phrase: phrase.phrase,
        author: phrase.author,
        x: Math.max(margin, Math.random() * maxX + centralOffsetX),
        y: Math.max(margin, Math.random() * maxY + centralOffsetY),
        rotation,
        fontSize,
        opacity: 0,
        addedTime: Date.now(),
      };
      attempts++;
    } while (
      attempts < 100 && // Mais tentativas para encontrar posição livre
      phrasePositions.some(existingPos => checkCollision(position, existingPos))
    );
    
    // Se não conseguiu encontrar posição livre, força uma posição válida no centro
    if (attempts >= 100) {
      console.warn('⚠️ Não foi possível encontrar posição livre, usando posição central forçada');
      position.x = Math.max(margin, (containerWidth - textDimensions.width) / 2);
      position.y = Math.max(margin, (containerHeight - textDimensions.height) / 2);
      position.addedTime = Date.now();
    }
    
    return position;
  };

  // Animar uma frase (fade in -> stay -> fade out)
  const animatePhrase = (phraseId: string) => {
    console.log(`🎭 Iniciando animação da frase: ${phraseId}`);
    
    // Fade in
    const startTime = Date.now();
    const fadeInAnimation = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / fadeInDuration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 2); // Ease out quadratic

      setPhrasePositions(prev => 
        prev.map(pos => 
          pos.id === phraseId ? { ...pos, opacity: easedProgress } : pos
        )
      );

      if (progress < 1) {
        requestAnimationFrame(fadeInAnimation);
      } else {
        // Após fade in, agendar fade out
        const stayDuration = animationDuration - fadeInDuration - fadeOutDuration;
        
        const fadeOutTimeout = setTimeout(() => {
          // Fade out
          const fadeOutStartTime = Date.now();
          const fadeOutAnimation = () => {
            const elapsed = Date.now() - fadeOutStartTime;
            const progress = Math.min(elapsed / fadeOutDuration, 1);
            const easedProgress = Math.pow(progress, 2); // Ease in quadratic

            setPhrasePositions(prev => 
              prev.map(pos => 
                pos.id === phraseId ? { ...pos, opacity: 1 - easedProgress } : pos
              )
            );

            if (progress < 1) {
              requestAnimationFrame(fadeOutAnimation);
            } else {
              // Remover frase após fade out
              setPhrasePositions(prev => prev.filter(pos => pos.id !== phraseId));
              setActivePhrases(prev => {
                const newSet = new Set(prev);
                newSet.delete(phraseId);
                return newSet;
              });
              console.log(`✅ Frase removida: ${phraseId}`);
            }
          };
          requestAnimationFrame(fadeOutAnimation);
        }, stayDuration);

        animationTimeoutRefs.current.set(phraseId, fadeOutTimeout);
      }
    };

    requestAnimationFrame(fadeInAnimation);
  };

  // Adicionar frase na tela
  const addPhraseToScreen = (phrase: Phrase) => {
    if (activePhrases.has(phrase.id)) return;
    
    console.log(`➕ Adicionando frase: ${phrase.phrase.substring(0, 30)}...`);
    
    // Gerar posição
    const newPosition = generateRandomPosition(phrase);
    
    // Adicionar à lista
    setPhrasePositions(prev => [...prev, newPosition]);
    setActivePhrases(prev => {
      const newSet = new Set(prev);
      newSet.add(phrase.id);
      return newSet;
    });

    // Iniciar animação
    setTimeout(() => animatePhrase(phrase.id), 100);
  };

  // Carregar parâmetros e frases iniciais e configurar ciclo
  useEffect(() => {
    let isActive = true;
    
    const initializeComponent = async () => {
      console.log('🚀 Inicializando componente...');
      
      // Primeiro, buscar parâmetros
      const maxPhrasesValue = await fetchParameters();
      
      if (!isActive) return;
      
      // Depois, carregar frases
      console.log('🚀 Carregando frases iniciais...');
      const loadedPhrases = await fetchPhrases();
      
      if (loadedPhrases.length > 0 && isActive) {
        // Adicionar primeira frase imediatamente
        setTimeout(() => {
          if (isActive) {
            addPhraseToScreen(loadedPhrases[0]);
          }
        }, 500);
        
        // Limpar interval anterior se existir
        if (addPhraseIntervalRef.current) {
          clearInterval(addPhraseIntervalRef.current);
        }
        
        // Adicionar mais frases gradualmente usando o maxPhrases dinâmico
        addPhraseIntervalRef.current = setInterval(() => {
          if (!isActive) return;
          
          console.log(`📊 Status: ${phrasePositions.length}/${maxPhrasesValue} frases na tela, ${activePhrases.size} ativas`);
          
          // Controle usando maxPhrases dinâmico
          if (phrasePositions.length < maxPhrasesValue && loadedPhrases.length > 0) {
            const availablePhrases = loadedPhrases.filter(p => !activePhrases.has(p.id));
            if (availablePhrases.length > 0) {
              const randomPhrase = availablePhrases[Math.floor(Math.random() * availablePhrases.length)];
              addPhraseToScreen(randomPhrase);
            }
          }
        }, 3000); // Intervalo aumentado
      }
    };

    initializeComponent();

    return () => {
      isActive = false;
      if (addPhraseIntervalRef.current) {
        clearInterval(addPhraseIntervalRef.current);
      }
    };
  }, []); // Dependências vazias para evitar loops

  // Cleanup dos timeouts ao desmontar
  useEffect(() => {
    return () => {
      // Limpar todos os timeouts de animação
      animationTimeoutRefs.current.forEach(timeout => clearTimeout(timeout));
      animationTimeoutRefs.current.clear();
      
      // Limpar interval de adição de frases
      if (addPhraseIntervalRef.current) {
        clearInterval(addPhraseIntervalRef.current);
      }
      
      console.log('🧹 Cleanup completo realizado');
    };
  }, []);

  // Função para limpar frase específica (chamada em caso de erro)
  const forceRemovePhrase = (phraseId: string) => {
    console.log(`🗑️ Removendo frase forçadamente: ${phraseId}`);
    
    // Limpar timeout se existir
    if (animationTimeoutRefs.current.has(phraseId)) {
      clearTimeout(animationTimeoutRefs.current.get(phraseId)!);
      animationTimeoutRefs.current.delete(phraseId);
    }
    
    // Remover da tela e dos estados
    setPhrasePositions(prev => prev.filter(pos => pos.id !== phraseId));
    setActivePhrases(prev => {
      const newSet = new Set(prev);
      newSet.delete(phraseId);
      return newSet;
    });
  };

  // Monitoramento para detectar frases "presas" na tela
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const currentTime = Date.now();
      const maxLifetime = animationDuration + 5000; // 5 segundos de tolerância
      
      phrasePositions.forEach(position => {
        // Se uma frase está na tela por muito tempo, forçar remoção
        const phraseAge = currentTime - (position as any).addedTime;
        if (phraseAge && phraseAge > maxLifetime) {
          console.warn(`⚠️ Frase ${position.id} está há muito tempo na tela, removendo forçadamente`);
          forceRemovePhrase(position.id);
        }
      });
    }, 10000); // Verificar a cada 10 segundos
    
    return () => clearInterval(cleanupInterval);
  }, [phrasePositions, animationDuration]);

  const handlePhraseClick = (phrase: PhrasePosition) => {
    if (onPhraseClick) {
      onPhraseClick({
        id: phrase.id,
        phrase: phrase.phrase,
        author: phrase.author,
      });
    }
  };

  return (
    <div
      ref={containerRef}
      className={`phrases-cloud ${className}`}
      style={{
        position: 'relative',
        width: '100vw',
        height: '100vh',
        backgroundColor: '#000',
        overflow: 'hidden',
      }}
    >
      {/* Loading indicator enquanto parâmetros não são carregados */}
      {!parametersLoaded && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '18px',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'center',
        }}>
          <div>🔧 Carregando parâmetros...</div>
        </div>
      )}

      {/* Renderizar frases apenas quando parâmetros estão carregados */}
      {parametersLoaded && phrasePositions.map((position) => (
        <div
          key={position.id}
          onClick={() => handlePhraseClick(position)}
          style={{
            position: 'absolute',
            left: position.x,
            top: position.y,
            fontSize: `${position.fontSize}px`,
            color: 'rgba(255, 255, 255, 0.9)',
            fontWeight: 'bold',
            fontFamily: 'Arial, sans-serif',
            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
            cursor: onPhraseClick ? 'pointer' : 'default',
            transform: `rotate(${position.rotation}deg)`,
            opacity: position.opacity,
            transition: 'opacity 0.3s ease',
            userSelect: 'none',
            whiteSpace: 'nowrap',
            zIndex: 10,
            transformOrigin: 'center center',
          }}
        >
          {position.phrase}
          {/* {position.author && (
            <span style={{
              fontSize: `${position.fontSize * 0.7}px`,
              opacity: 0.7,
              marginLeft: position.rotation === 0 ? '10px' : '0',
              display: position.rotation === 0 ? 'inline' : 'block',
            }}>
              — {position.author}
            </span>
          )} */}
        </div>
      ))}
      
      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed',
          top: 10,
          left: 10,
          color: 'white',
          backgroundColor: 'rgba(0,0,0,0.7)',
          padding: '10px',
          borderRadius: '5px',
          fontSize: '12px',
          zIndex: 1000,
        }}>
          <div>Parâmetros carregados: {parametersLoaded ? '✅' : '⏳'}</div>
          <div>MaxPhrases (API): {maxPhrases}</div>
          <div>Frases carregadas: {phrases.length}</div>
          <div>Frases na tela: {phrasePositions.length}/{maxPhrases}</div>
          <div>Frases ativas: {activePhrases.size}</div>
          <div>Viewport: {window.innerWidth}x{window.innerHeight}</div>
          {phrasePositions.length > 0 && (
            <div style={{ marginTop: '5px', fontSize: '10px' }}>
              Últimas posições: {phrasePositions.slice(-2).map(p => 
                `(${Math.round(p.x)},${Math.round(p.y)})`
              ).join(' ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PhrasesCloudSimple;
